import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { riskCategoryColor } from './riskIndicatorConfig';
import { tierFor } from './riskTiers';
import { formatPoints, formatWholeRiskValue as formatRiskValue, gradeLabel } from './riskFormat';
import { lowerRiskPeers, peerBenchmark } from './peerBenchmark';
import { TierChip } from './RiskRankingTable';
import TrendSparkline from './TrendSparkline';
import { formatGrades, letterGradeColor } from '../maps/mapHoverCards';

const isYes = (value) => String(value || '').toLowerCase() === 'yes';

const Section = ({ title, children, subtitle }) => (
  <Box sx={{ px: 2.5, py: 1.75 }}>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Box>
);

const Stat = ({ label, value, sub, color }) => (
  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, px: 1.25, py: 1, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ color: color || 'text.primary', lineHeight: 1.3 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const GRID_SX = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1 };

const changeColor = (value) => {
  if (value === null || value === undefined) return undefined;
  if (value <= -0.05) return '#cc2222';
  if (value >= 0.05) return '#2a9d2a';
  return undefined;
};

const vsStateColor = (flag) => {
  const value = String(flag || '').toLowerCase();
  if (value === 'below') return '#cc2222';
  if (value === 'above' || value === 'at or above') return '#2a9d2a';
  return undefined;
};

const ScoreBar = ({ score, color }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Box sx={{ width: 60, height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ width: `${((score || 0) / 5) * 100}%`, height: '100%', bgcolor: color }} />
    </Box>
    <Typography variant="caption" fontWeight={600}>
      {score === null ? '—' : score.toFixed(1)}
    </Typography>
  </Stack>
);

const SchoolRiskProfile = ({ row, indicators, peerRowsById, pair, meta, qualityMethodology, mapPath = null, onClose }) => {
  const benchmark = useMemo(
    () => (row ? peerBenchmark(row, peerRowsById, indicators) : null),
    [row, peerRowsById, indicators]
  );
  const school = row?.school;
  const nearbyLower = row ? lowerRiskPeers(row, benchmark) : [];
  const hasLetterGrade = (indicators || []).some((indicator) => indicator.id === 'letterGrade');
  const assessmentPrefix = meta?.assessment ? `${meta.assessment} ` : '';

  return (
    <Drawer anchor="right" open={Boolean(row)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 600 } } }}>
      {row && (
        <Box>
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5, position: 'relative' }}>
            <IconButton aria-label="Close risk profile" onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
              <CloseIcon />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Rank {row.rank} by enrollment risk
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ pr: 5, lineHeight: 1.3 }}>
              {school.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[school.city, school.county, school.schoolDistrictName].filter(Boolean).join(' · ')}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
              <TierChip tier={row.tier} points={row.points} size="medium" />
              {school.gradesOffered && (
                <Chip size="small" variant="outlined" label={`Grades ${formatGrades(school.gradesOffered)}`} />
              )}
              {isYes(school.steadyState) && <Chip size="small" variant="outlined" label="Steady State" />}
              {isYes(school.phaseInOut) && <Chip size="small" variant="outlined" label="Phasing In/Out" />}
              {school.yearsOfData < 3 && <Chip size="small" color="warning" variant="outlined" label={`Limited history (${school.yearsOfData} yrs)`} />}
            </Stack>
            {mapPath && (
              <Button
                component={RouterLink}
                to={`${mapPath}?color=risk&school=${encodeURIComponent(school.id)}`}
                size="small"
                variant="outlined"
                startIcon={<MapOutlinedIcon />}
                sx={{ mt: 1.25, textTransform: 'none' }}
              >
                View on charter risk map
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {row.coverage.scored} of {row.coverage.total} measures scored
              {row.coverage.scored < row.coverage.total ? '; missing measures are excluded from the blend.' : '.'}
            </Typography>
          </Box>
          <Divider />

          <Section title="What drives the score" subtitle="Each measure scores 1 (lowest risk) to 5 (highest risk).">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Measure</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Value
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Risk score</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Weight
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.contributions.map((item) => {
                  const indicator = indicators.find((i) => i.id === item.id);
                  return (
                    <TableRow key={item.id}>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{indicator?.label || item.label}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {formatRiskValue(item.value, indicator?.format)}
                      </TableCell>
                      <TableCell>
                        <ScoreBar score={item.score} color={riskCategoryColor(item.category)} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                        {item.missing ? '—' : `${Math.round(item.effectiveWeight * 100)}%`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Section>
          <Divider />

          <Section title="Enrollment history" subtitle={`All grades, ${meta.peakStartYear || '2019-2020'} onward. Red dot = peak.`}>
            <Box sx={{ ...GRID_SX, mb: 1 }}>
              <Stat label={`Enrollment (${school.enrollmentYear || ''})`} value={formatRiskValue(school.enrollment, 'integer')} />
              <Stat
                label={`5-year change (since ${school.enrollmentBaseYear || ''})`}
                value={school.enrollmentChange5y === null ? 'New since then' : formatRiskValue(school.enrollmentChange5y, 'signedPercent')}
                color={changeColor(school.enrollmentChange5y)}
              />
              <Stat
                label={`From peak (${school.peakYear || ''})`}
                value={formatRiskValue(school.pctFromPeak, 'signedPercent')}
                sub={`${formatRiskValue(school.numFromPeak, 'signedInteger')} students vs ${formatRiskValue(school.peakEnrollment, 'integer')}`}
                color={changeColor(school.pctFromPeak)}
              />
            </Box>
            <TrendSparkline series={school.enrollmentSeries} ariaLabel="Enrollment by year" />
          </Section>
          <Divider />

          <Section title={`Entry grade trend (${gradeLabel(school.entryGrade)})`} subtitle="The grade where most students enter. Shrinking entry classes lead overall declines.">
            <Box sx={{ ...GRID_SX, mb: 1 }}>
              <Stat
                label={`From entry-grade peak (${school.entryPeakYear || ''})`}
                value={formatRiskValue(school.pctFromEntryPeak, 'signedPercent')}
                sub={`Peak ${formatRiskValue(school.entryPeak, 'integer')} students`}
                color={changeColor(school.pctFromEntryPeak)}
              />
            </Box>
            <TrendSparkline series={school.entryGradeSeries} color="#4338ca" ariaLabel="Entry grade enrollment by year" />
          </Section>
          <Divider />

          <Section
            title="Cohort matriculation"
            subtitle="Students in a grade this year ÷ students in the grade below last year. Below 100% means students are leaving between grades."
          >
            <Box sx={{ ...GRID_SX, mb: 1 }}>
              <Stat
                label={`All grades (${school.matriculationYear || ''})`}
                value={formatRiskValue(school.matriculationRate, 'percent')}
                color={school.matriculationRate !== null && school.matriculationRate < 0.9 ? '#cc2222' : undefined}
              />
              <Stat label="3-year average" value={formatRiskValue(school.matriculationAvg, 'percent')} />
            </Box>
            {school.matriculationByGrade?.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Grade to grade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Last year
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      This year
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Rate
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {school.matriculationByGrade.map((g) => (
                    <TableRow key={g.toGrade}>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {gradeLabel(g.fromGrade)} → {gradeLabel(g.toGrade)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                        {g.from}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                        {g.to}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.78rem', color: g.rate < 0.9 ? '#cc2222' : undefined, fontWeight: 600 }}>
                        {formatRiskValue(g.rate, 'percent')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
          <Divider />

          <Section title="Academic quality" subtitle={qualityMethodology}>
            <Box sx={GRID_SX}>
              {hasLetterGrade && (
                <Stat
                  label={`Letter grade${school.accountabilityYear ? ` (${school.accountabilityYear})` : ''}`}
                  value={school.letterGrade || 'Not rated'}
                  sub={school.abQuality == null ? null : school.abQuality === 1 ? 'A or B: lower risk' : 'C, D or F: higher risk'}
                  color={letterGradeColor(school.letterGrade)}
                />
              )}
              <Stat
                label={`${assessmentPrefix}ELA % met (${meta.proficiencyYear || 'latest'})`}
                value={formatRiskValue(school.elaProficiency, 'percent')}
                sub={school.elaProficiencyVsStateNonFrl ? `${school.elaProficiencyVsStateNonFrl} state non-low-income` : null}
                color={vsStateColor(school.elaProficiencyVsStateNonFrl)}
              />
              <Stat
                label={`${assessmentPrefix}Math % met (${meta.proficiencyYear || 'latest'})`}
                value={formatRiskValue(school.mathProficiency, 'percent')}
                sub={school.mathProficiencyVsStateNonFrl ? `${school.mathProficiencyVsStateNonFrl} state non-low-income` : null}
                color={vsStateColor(school.mathProficiencyVsStateNonFrl)}
              />
            </Box>
          </Section>
          <Divider />

          <Section title="Forward-looking enrollment">
            <Box sx={GRID_SX}>
              <Stat
                label={`ProjectX midpoint (${meta.futureYear || '5 years out'})`}
                value={formatRiskValue(school.projectedEnrollment5y, 'integer')}
                sub={`${formatRiskValue(school.projectedChange5y, 'signedPercent')} vs today`}
                color={changeColor(school.projectedChange5y)}
              />
              <Stat
                label={`District K-12 population (${meta.esriYear || ''}→${meta.esriFutureYear || ''})`}
                value={formatRiskValue(school.areaPopChange2031, 'signedPercent')}
                color={changeColor(school.areaPopChange2031)}
              />
              {school.driveTimePopChange !== null && school.driveTimePopChange !== undefined && (
                <Stat
                  label={`${school.driveTimeMinutes || 10}-min drive-time population`}
                  value={formatRiskValue(school.driveTimePopChange, 'signedPercent')}
                  sub="Context only; not scored"
                  color={changeColor(school.driveTimePopChange)}
                />
              )}
            </Box>
          </Section>
          <Divider />

          <Section
            title="Compared with district schools"
            subtitle={`${benchmark.peers.length} traditional public school${benchmark.peers.length === 1 ? '' : 's'} serving the same grades within a ${meta.peerDriveTimeMinutes || 10}-minute drive, scored with the same model.`}
          >
            {benchmark.peers.length ? (
              <>
                <Box sx={{ ...GRID_SX, mb: 1.5 }}>
                  <Stat label="This charter" value={formatPoints(row.points)} sub={row.tier.label} color={row.tier.color} />
                  <Stat
                    label="Peer median risk"
                    value={formatPoints(benchmark.medianPoints)}
                    sub={tierFor(benchmark.medianPoints).label}
                    color={tierFor(benchmark.medianPoints).color}
                  />
                </Box>
                <Table size="small" sx={{ mb: 1.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Measure</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Charter
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Peer median
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {indicators.map((indicator) => (
                      <TableRow key={indicator.id}>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{indicator.shortLabel}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {formatRiskValue(school[indicator.sourceField], indicator.format)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {formatRiskValue(benchmark.medians[indicator.id], indicator.format)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>District school</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Miles
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Enrollment
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Risk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {benchmark.peers.map(({ row: peer, distanceMiles }) => (
                      <TableRow key={peer.school.id}>
                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          {peer.school.name}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {peer.school.schoolDistrictName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {distanceMiles?.toFixed(1)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {formatRiskValue(peer.school.enrollment, 'integer')}
                        </TableCell>
                        <TableCell>
                          <TierChip tier={peer.tier} points={peer.points} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No comparable district schools found nearby.
              </Typography>
            )}
          </Section>
          <Divider />

          <Section title="Nearby lower-risk schools">
            {pair?.nearestCharter && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Nearest low-risk charter: <b>{pair.nearestCharter.row.school.name}</b> (
                {pair.nearestCharter.miles.toFixed(1)} mi, {formatPoints(pair.nearestCharter.row.points)})
              </Typography>
            )}
            {nearbyLower.length ? (
              nearbyLower.map(({ row: peer, distanceMiles }) => (
                <Typography key={peer.school.id} variant="body2" sx={{ mb: 0.5 }}>
                  {peer.school.name} — {distanceMiles.toFixed(1)} mi, {peer.tier.label} ({formatPoints(peer.points)})
                </Typography>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                None of this school&apos;s district peers score lower risk.
              </Typography>
            )}
          </Section>
        </Box>
      )}
    </Drawer>
  );
};

export default SchoolRiskProfile;
